import React from "react";
import PropTypes from "prop-types";
import Spinner from "./Spinner";
import { ACTION_DISPLAY_TYPES } from "../../constants/enums";
import DynamicIcon from "./Icons/DynamicIcon";
import { ICON_SVG_PATH } from "../../constants/iconsvg";

/**
 * Generates a button
 */
export class Action extends React.Component {
  constructor(props) {
    super(props);
  }

  renderIcon(icon, className, isReceipt = false) {
    if (!icon && !isReceipt) return null;
    // Existing behavior: a string is a URL → <img>
    if (typeof icon === "string") {
      return <img src={icon} className={className} />;
    }
    // New behavior: a React element (e.g. <PhoneIcon />) → render as-is
    if (React.isValidElement(icon)) {
      return className
        ? <span className={`${className} flex-row justify-content-center align-items-center chevron-wrapper`}>{icon}</span>
        : icon;
    }

    if (isReceipt) {
      return (
        <DynamicIcon
          strokeColor="#05399B"
          fillColor="#05399B"
          strokeWidth={0}
          size={24}
          path={ICON_SVG_PATH.DOWNLOAD}
          className="mr-2"
        />
      );
    }
    return null;
  }

  renderTitle() {
    const { loading, title, titleClassName } = this.props;
    if (loading) return <Spinner show={true} />;
    if (titleClassName) return <span className={titleClassName}>{title}</span>;
    return title;
  }

  render() {
    let styleClass;

    switch (this.props.displayType) {
      case ACTION_DISPLAY_TYPES.PRIMARY:
        styleClass = "btn-primary";
        break;
      case ACTION_DISPLAY_TYPES.SECONDARY:
        styleClass = "btn-secondary";
        break;
      case ACTION_DISPLAY_TYPES.TERTIARY:
        styleClass = "btn-tertiary";
        break;
      case ACTION_DISPLAY_TYPES.LINK:
        styleClass = "btn-link";
        break;
      case ACTION_DISPLAY_TYPES.RECEIPT:
        styleClass = "btn-receipt";
        break;
      case ACTION_DISPLAY_TYPES.TEXT:
        styleClass = "btn-text";
        break;
      default:
        styleClass = "btn-primary";
        break;
    }

    let loadingClass = this.props.loading ? "loading" : "";
    let blockClass = this.props.block ? "block" : "";

    // `small` is retained for back-compat; `size` wins when both are passed
    let sizeClass = "";
    if (this.props.size) sizeClass = `btn-${this.props.size}`;
    else if (this.props.small) sizeClass = "btn-sm";

    let widthClass = this.props.width ? `btn-${this.props.width}` : "";

    return (
      <button
        data-cy={this.props.dataCy}
        data-testid={this.props.testId || "action-button"}
        onClick={this.props.onClick}
        className={`btn ${styleClass} ${loadingClass} ${sizeClass} ${widthClass} ${blockClass} ${this.props.className}`}
        autoFocus={this.props.autoFocus}
        disabled={this.props.disabled}
        type={this.props.type}
        form={this.props.formId}
      >
        {this.renderIcon(this.props.iconLeft, "icon-left", this.props.isReceipt)}
        {this.renderTitle()}
        {this.renderIcon(this.props.iconRight, "icon-right")}
      </button>
    );
  }
}

Action.propTypes = {
  /** Button title */
  title: PropTypes.string,
  /** Image url if no link text is provided */
  imageSrc: PropTypes.string,
  /** Callback on click.
   * e: The button event
   * action: The action object that was used to generate the action
   * ) */
  onClick: PropTypes.func,
  /** Sets the style  */
  displayType: PropTypes.number,
  /** Sets the loading state */
  loading: PropTypes.bool,
  /** Sets the autoFocus property on the button */
  autoFocus: PropTypes.bool,
  /** Creates a small version of the button. Deprecated — use `size="sm"` */
  small: PropTypes.bool,
  /** Height variant: sm 28px, md 36px, lg 44px (default) */
  size: PropTypes.oneOf(["sm", "md", "lg"]),
  /** Min-width variant: auto (content), narrow 90px, short 120px, wide 160px. Omit for the 200px default */
  width: PropTypes.oneOf(["auto", "narrow", "short", "wide"]),
  /** Determines if the button is disabled */
  disabled: PropTypes.bool,
  /** Determines if the button is larger and sits on it's own line */
  block: PropTypes.bool,
  /** Displays the given icon on the left side of the button */
  iconLeft: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Displays the given icon on the right side of the button */
  iconRight: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /** Addition css classes */
  className: PropTypes.string,
  /** data-cy attribute value for UI testing */
  dataCy: PropTypes.string,
  /** button type, to allow type to be 'submit' */
  type: PropTypes.string,
  /** form id that can be passed in to allow Action to submit form when not located within <form> element */
  formId: PropTypes.string,
  /** test id attribute for testing */
  testId: PropTypes.string,
  /** Sets if is a receipt button */
  isReceipt: PropTypes.bool,
  /** Custom class for the title element */
  titleClassName: PropTypes.string,
};

export default Action;
